import { useState, useEffect, useMemo, useCallback } from 'react';
import { ChevronRight, Calendar, FileText, AlertTriangle } from 'lucide-react';
import type {
  ParticipantFlagWithDetails,
  ParticipantFlagCategory,
  Participant,
} from '../../../../types';
import type { GroupWithMembers } from '../../../../services/api/groups';
import { cohortsApi } from '../../../../services/api/cohorts';
import { participantsApi } from '../../../../services/api/participants';
import { groupsApi } from '../../../../services/api/groups';
import { getAvatarColor } from '../../../../utils/avatarHelpers';
import { getFlagSeverity, getHighestSeverity, compareBySeverity } from '../../../../config/severityConfig';
import type { FlagSeverity } from '../../../../config/severityConfig';
import { DataLibraryDrawer } from '../../../../components/DataLibrary/DataLibraryDrawer';
import classes from './ParticipantHealthDrillDown.module.css';

// ============================================================================
// Types
// ============================================================================

type TabKey = 'flagged' | 'allParticipants' | 'byGroup';

interface ParticipantHealthDrillDownProps {
  cohortId: string;
  flaggedCount: number;
  onAddTask?: (defaults: { title: string; description: string }) => void;
  onTabChange?: () => void;
  onOpenFollowUps?: (options?: { sessionId?: string; participantId?: string }) => void;
}

interface ParticipantWithFlags {
  participantId: string;
  name: string;
  initials: string;
  groupName: string | null;
  flags: ParticipantFlagWithDetails[];
  attendancePercent: number | null;
  avgEngagement: number | null;
  status: string;
  withdrawnAt: string | null;
}

// ============================================================================
// Constants
// ============================================================================

const TAB_CONFIG: { key: TabKey; label: string }[] = [
  { key: 'flagged', label: 'Flagged' },
  { key: 'allParticipants', label: 'All Participants' },
  { key: 'byGroup', label: 'By Group' },
];

const FILTER_CHIPS: { key: ParticipantFlagCategory | 'all'; label: string }[] = [
  { key: 'all', label: 'All Flags' },
  { key: 'attendance', label: 'Attendance Issues' },
  { key: 'participant_feedback', label: 'Participant Feedback' },
  { key: 'engagement', label: 'Engagement' },
  { key: 'coach_flag', label: 'Coach Flag' },
];

const SEVERITY_PILL_CLASS: Record<FlagSeverity, string> = {
  high: classes.flagSeverityHigh,
  medium: classes.flagSeverityMedium,
  low: classes.flagSeverityLow,
};

const SEVERITY_ROW_CLASS: Record<FlagSeverity, string> = {
  high: classes.participantRowSeverityHigh,
  medium: classes.participantRowSeverityMedium,
  low: classes.participantRowSeverityLow,
};

const FLAG_CATEGORY_ICON: Record<ParticipantFlagCategory, React.ReactNode> = {
  attendance: <Calendar size={12} />,
  participant_feedback: <FileText size={12} />,
  engagement: <AlertTriangle size={12} />,
  coach_flag: <AlertTriangle size={12} />,
};

function getInitials(name: string): string {
  return name
    .split(' ')
    .map(n => n.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('');
}

const MAX_VISIBLE_FLAGS = 3;

// ============================================================================
// Sub-components
// ============================================================================

function FlagPill({ flag }: { flag: ParticipantFlagWithDetails }) {
  const icon = FLAG_CATEGORY_ICON[flag.flag_category];
  const severity = getFlagSeverity(flag.flag_category);
  const colorClass = SEVERITY_PILL_CLASS[severity];

  return (
    <span className={`${classes.flagPill} ${colorClass}`}>
      {icon} {flag.flag_reason}
    </span>
  );
}

function WithdrawnPillInline({ withdrawnAt }: { withdrawnAt: string | null }) {
  const label = (() => {
    if (!withdrawnAt) return 'WITHDRAWN';
    const date = new Date(withdrawnAt);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const currentYear = new Date().getFullYear();
    if (date.getFullYear() !== currentYear) {
      return `WITHDRAWN ${month}/${day}/${String(date.getFullYear()).slice(-2)}`;
    }
    return `WITHDRAWN ${month}/${day}`;
  })();

  return (
    <span style={{
      display: 'inline-block',
      backgroundColor: '#E8E8E8',
      color: '#444444',
      fontSize: 10,
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.3px',
      padding: '1px 6px',
      borderRadius: 3,
      marginLeft: 6,
    }}>
      {label}
    </span>
  );
}

function ParticipantRow({
  participant,
  showStats,
  onAddTask,
  onViewParticipant,
}: {
  participant: ParticipantWithFlags;
  showStats?: boolean;
  onAddTask?: (p: ParticipantWithFlags) => void;
  onViewParticipant?: (participantId: string) => void;
}) {
  const visibleFlags = participant.flags.slice(0, MAX_VISIBLE_FLAGS);
  const truncatedCount = participant.flags.length - MAX_VISIBLE_FLAGS;

  const isFlagged = participant.flags.length > 0;
  const isWithdrawn = participant.status === 'withdrawn';
  const rowSeverityClass = isFlagged && !isWithdrawn ? SEVERITY_ROW_CLASS[getHighestSeverity(participant.flags)] : '';

  return (
    <div className={`${classes.participantRow} ${rowSeverityClass}`} style={isWithdrawn ? { opacity: 0.5 } : undefined}>
      <div
        className={classes.avatar}
        style={{ backgroundColor: getAvatarColor(participant.name) }}
      >
        {participant.initials}
      </div>

      <div className={classes.participantInfo}>
        <div className={classes.participantName}>
          {participant.name}
          {isWithdrawn && <WithdrawnPillInline withdrawnAt={participant.withdrawnAt} />}
        </div>
        {participant.groupName && (
          <div className={classes.participantGroup}>{participant.groupName}</div>
        )}
      </div>

      {showStats && (
        <div className={classes.participantStats}>
          <span className={classes.statItem}>
            <span className={classes.statValue}>
              {participant.attendancePercent != null ? `${participant.attendancePercent}%` : 'N/A'}
            </span>{' '}
            attendance
          </span>
          <span className={classes.statItem}>
            <span className={classes.statValue}>
              {participant.avgEngagement != null ? participant.avgEngagement.toFixed(1) : 'N/A'}
            </span>{' '}
            engagement
          </span>
        </div>
      )}

      <div className={classes.flagPills}>
        {visibleFlags.map(flag => (
          <FlagPill key={flag.id} flag={flag} />
        ))}
        {truncatedCount > 0 && (
          <span className={classes.flagTruncation}>+{truncatedCount}</span>
        )}
      </div>

      {onAddTask && (
        <button
          className={classes.addTaskButton}
          onClick={() => onAddTask(participant)}
        >
          Add Task
        </button>
      )}
      {onViewParticipant && (
        <button
          className={classes.viewAllLink}
          onClick={() => onViewParticipant(participant.participantId)}
        >
          view <ChevronRight size={14} />
        </button>
      )}
    </div>
  );
}

function GroupSection({
  group,
  participants,
  onAddTask,
  onViewParticipant,
}: {
  group: GroupWithMembers;
  participants: ParticipantWithFlags[];
  onAddTask?: (p: ParticipantWithFlags) => void;
  onViewParticipant?: (participantId: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  const avgAttendance = useMemo(() => {
    const vals = participants.filter(p => p.attendancePercent != null).map(p => p.attendancePercent!);
    return vals.length > 0 ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null;
  }, [participants]);

  const avgEngagement = useMemo(() => {
    if (group.avg_engagement != null) return group.avg_engagement;
    const vals = participants.filter(p => p.avgEngagement != null).map(p => p.avgEngagement!);
    return vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  }, [group.avg_engagement, participants]);

  const flaggedCount = useMemo(() => participants.filter(p => p.flags.length > 0).length, [participants]);

  return (
    <div className={classes.groupSection}>
      <button className={classes.groupHeader} onClick={() => setExpanded(prev => !prev)}>
        <div className={classes.groupHeaderLeft}>
          <ChevronRight
            size={16}
            className={`${classes.groupChevron} ${expanded ? classes.groupChevronOpen : ''}`}
          />
          <span className={classes.groupName}>{group.name}</span>
          {flaggedCount > 0 && (
            <span className={classes.groupFlaggedPill}>
              {flaggedCount} flagged participant{flaggedCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className={classes.groupStats}>
          <span>
            Avg Attendance:{' '}
            <span className={classes.groupStatValue}>
              {avgAttendance != null ? `${avgAttendance}%` : 'N/A'}
            </span>
          </span>
          <span>
            Avg Engagement:{' '}
            <span className={classes.groupStatValue}>
              {avgEngagement != null ? avgEngagement.toFixed(1) : 'N/A'}
            </span>
          </span>
        </div>
      </button>

      {expanded && (
        <div className={classes.groupMembers}>
          {participants.length === 0 ? (
            <div className={classes.emptyState}>No participants in this group</div>
          ) : (
            participants.map(p => (
              <ParticipantRow key={p.participantId} participant={p} onAddTask={onAddTask} onViewParticipant={onViewParticipant} />
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function ParticipantHealthDrillDown({
  cohortId,
  flaggedCount,
  onAddTask,
  onTabChange,
}: ParticipantHealthDrillDownProps) {
  const [activeTab, setActiveTab] = useState<TabKey>('flagged');
  const [activeFilter, setActiveFilter] = useState<ParticipantFlagCategory | 'all'>('all');
  const [flags, setFlags] = useState<ParticipantFlagWithDetails[]>([]);
  const [allParticipants, setAllParticipants] = useState<Participant[]>([]);
  const [groups, setGroups] = useState<GroupWithMembers[]>([]);
  const [loading, setLoading] = useState(true);

  // Data Library drawer
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [drawerLayoutKey, setDrawerLayoutKey] = useState(0);
  const [drawerTab, setDrawerTab] = useState('participants');
  const [drawerParticipantsConfig, setDrawerParticipantsConfig] = useState<
    Parameters<typeof DataLibraryDrawer>[0]['participantsConfig']
  >(undefined);
  // MED-764: Participant ID for auto-opening detail modal in Data Library
  const [drawerParticipantId, setDrawerParticipantId] = useState<string | undefined>(undefined);

  const openDataLibrary = useCallback((tab: TabKey) => {
    setDrawerTab('participants');
    setDrawerParticipantId(undefined);
    switch (tab) {
      case 'flagged':
        setDrawerParticipantsConfig({ quickFilters: ['flagged'], sort: { key: 'flag_severity', order: 'DESC' } });
        break;
      case 'allParticipants':
        setDrawerParticipantsConfig({ sort: { key: 'flag_severity', order: 'DESC' } });
        break;
      case 'byGroup':
        setDrawerParticipantsConfig({ groupBy: [{ field: 'group', order: 'alphabetical' }], sort: { key: 'flag_severity', order: 'DESC' } });
        break;
    }
    setDrawerLayoutKey(k => k + 1);
    setDrawerOpened(true);
  }, []);

  // MED-764: Open Data Library with participant detail modal
  const handleViewParticipant = useCallback((participantId: string) => {
    setDrawerTab('participants');
    setDrawerParticipantsConfig({ sort: { key: 'flag_severity', order: 'DESC' } });
    setDrawerParticipantId(participantId);
    setDrawerLayoutKey(k => k + 1);
    setDrawerOpened(true);
  }, []);

  // Fetch data on mount
  useEffect(() => {
    let cancelled = false;

    async function fetchAllParticipants(cohortId: string): Promise<Participant[]> {
      const all: Participant[] = [];
      let page = 1;
      const limit = 100;
      let hasMore = true;

      while (hasMore) {
        const response = await participantsApi.list(cohortId, { page, limit });
        all.push(...response.data);
        hasMore = page < response.pagination.totalPages;
        page++;
      }

      return all;
    }

    async function fetchData() {
      setLoading(true);
      try {
        const [flagsData, participantsData, groupsData] = await Promise.all([
          cohortsApi.getParticipantFlags(cohortId),
          fetchAllParticipants(cohortId),
          groupsApi.list(cohortId),
        ]);

        if (!cancelled) {
          setFlags(flagsData);
          setAllParticipants(participantsData);
          setGroups(groupsData.data);
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed to load participant health data:', err);
        if (!cancelled) setLoading(false);
      }
    }

    fetchData();
    return () => { cancelled = true; };
  }, [cohortId]);

  // Build flag lookup: participantId -> flags[]
  const flagsByParticipant = useMemo(() => {
    const map = new Map<string, ParticipantFlagWithDetails[]>();
    for (const flag of flags) {
      const existing = map.get(flag.participant_id) || [];
      existing.push(flag);
      map.set(flag.participant_id, existing);
    }
    return map;
  }, [flags]);

  // Build ParticipantWithFlags for each participant
  const buildParticipantWithFlags = useCallback(
    (p: Participant): ParticipantWithFlags => ({
      participantId: p.user_id,
      name: `${p.first_name} ${p.last_name}`,
      initials: getInitials(`${p.first_name} ${p.last_name}`),
      groupName: p.group_name,
      flags: flagsByParticipant.get(p.user_id) || [],
      attendancePercent: p.attendance_percentage,
      avgEngagement: null, // Individual engagement not available on Participant type
      status: p.status,
      withdrawnAt: p.withdrawn_at,
    }),
    [flagsByParticipant],
  );

  // Flagged participants (filtered by category)
  const flaggedParticipants = useMemo(() => {
    const flaggedIds = new Set(flags.map(f => f.participant_id));
    const participants = allParticipants
      .filter(p => flaggedIds.has(p.user_id))
      .map(buildParticipantWithFlags);

    let result = participants;

    if (activeFilter !== 'all') {
      // Filter to only participants with flags in the selected category
      result = result
        .map(p => ({
          ...p,
          flags: p.flags.filter(f => f.flag_category === activeFilter),
        }))
        .filter(p => p.flags.length > 0);
    }

    // Sort by highest severity: High → Medium → Low
    return [...result].sort((a, b) => compareBySeverity(a.flags, b.flags));
  }, [flags, allParticipants, buildParticipantWithFlags, activeFilter]);

  // All participants with flags attached
  const allParticipantsWithFlags = useMemo(
    () => allParticipants.map(buildParticipantWithFlags),
    [allParticipants, buildParticipantWithFlags],
  );

  // Groups with participant data
  const groupsWithParticipants = useMemo(() => {
    return groups.map(group => {
      const memberUserIds = new Set(group.members.map(m => m.user_id));
      const groupParticipants = allParticipants
        .filter(p => memberUserIds.has(p.user_id))
        .map(buildParticipantWithFlags);
      return { group, participants: groupParticipants };
    });
  }, [groups, allParticipants, buildParticipantWithFlags]);

  // Total flagged count (unfiltered — for tab badge)
  const totalFlaggedCount = useMemo(() => {
    const flaggedIds = new Set(flags.map(f => f.participant_id));
    return allParticipants.filter(p => flaggedIds.has(p.user_id)).length;
  }, [flags, allParticipants]);

  // Tab counts
  const tabCounts: Record<TabKey, number> = {
    flagged: totalFlaggedCount,
    allParticipants: allParticipants.length,
    byGroup: groups.length,
  };

  // Handle add task with pre-population
  const handleAddTask = useCallback(
    (participant: ParticipantWithFlags) => {
      if (!onAddTask) return;

      // If participant has flags, use the first flag for pre-population
      const primaryFlag = participant.flags[0];
      if (primaryFlag) {
        if (primaryFlag.source_type === 'coach') {
          onAddTask({
            title: `Coach Flag: ${participant.name}`,
            description: `Coach flagged ${participant.name} in Session ${primaryFlag.session_number ?? 'N/A'}.\n\nReason: ${primaryFlag.flag_reason}`,
          });
        } else {
          // SGOS system flag - use flag_reason for description
          onAddTask({
            title: `${participant.name} needs attention`,
            description: `${primaryFlag.flag_reason}\n\nSource: Health Alert System`,
          });
        }
      } else {
        onAddTask({
          title: `Follow up: ${participant.name}`,
          description: `Follow up with ${participant.name}.`,
        });
      }
    },
    [onAddTask],
  );

  if (loading) {
    return (
      <div className={classes.drillDown}>
        <div className={classes.loading}>Loading participant health data...</div>
      </div>
    );
  }

  return (
    <>
    <DataLibraryDrawer
      opened={drawerOpened}
      onClose={() => setDrawerOpened(false)}
      defaultCohortId={cohortId}
      defaultTab={drawerTab}
      participantsConfig={drawerParticipantsConfig}
      layoutKey={drawerLayoutKey}
      defaultParticipantId={drawerParticipantId}
    />
    <div className={classes.drillDown}>
      {/* Header */}
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <span className={classes.headerTitle}>Participant Health</span>
          {flaggedCount > 0 && (
            <span className={classes.flaggedBadge}>
              {flaggedCount} need attention
            </span>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className={classes.tabs}>
        {TAB_CONFIG.map(({ key, label }) => {
          const count = tabCounts[key];
          const isActive = activeTab === key;
          const isFlaggedTab = key === 'flagged';
          return (
            <button
              key={key}
              className={`${classes.tab} ${isActive ? classes.tabActive : ''}`}
              onClick={() => { setActiveTab(key); onTabChange?.(); }}
            >
              {label}
              <span className={isFlaggedTab && count > 0 ? classes.tabFlaggedCount : classes.tabCount}>{count}</span>
            </button>
          );
        })}
        <button
          className={classes.viewAllLink}
          style={{ marginLeft: 'auto' }}
          onClick={() => openDataLibrary(activeTab)}
        >
          View in Data Library <ChevronRight size={14} />
        </button>
      </div>

      {/* Filter Chips (Flagged tab only) */}
      {activeTab === 'flagged' && (
        <div className={classes.filterChips}>
          {FILTER_CHIPS.map(({ key, label }) => (
            <button
              key={key}
              className={`${classes.filterChip} ${activeFilter === key ? classes.filterChipActive : ''}`}
              onClick={() => { setActiveFilter(key); onTabChange?.(); }}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Flagged Tab */}
      {activeTab === 'flagged' && (
        <div className={classes.tabContent}>
          <div className={classes.participantList}>
            {flaggedParticipants.length === 0 ? (
              <div className={classes.emptyState}>No flagged participants</div>
            ) : (
              flaggedParticipants.map(p => (
                <ParticipantRow
                  key={p.participantId}
                  participant={p}
                  onAddTask={handleAddTask}
                  onViewParticipant={handleViewParticipant}
                />
              ))
            )}
          </div>
        </div>
      )}

      {/* All Participants Tab */}
      {activeTab === 'allParticipants' && (
        <div className={classes.tabContent}>
          <div className={classes.participantList}>
            {allParticipantsWithFlags.length === 0 ? (
              <div className={classes.emptyState}>No participants found</div>
            ) : (
              allParticipantsWithFlags.map(p => (
                <ParticipantRow
                  key={p.participantId}
                  participant={p}
                  showStats
                  onAddTask={handleAddTask}
                  onViewParticipant={handleViewParticipant}
                />
              ))
            )}
          </div>
        </div>
      )}

      {/* By Group Tab */}
      {activeTab === 'byGroup' && (
        <div className={classes.tabContent}>
          {groupsWithParticipants.length === 0 ? (
            <div className={classes.emptyState}>No groups found</div>
          ) : (
            groupsWithParticipants.map(({ group, participants }) => (
              <GroupSection
                key={group.id}
                group={group}
                participants={participants}
                onAddTask={handleAddTask}
                onViewParticipant={handleViewParticipant}
              />
            ))
          )}
        </div>
      )}
    </div>
    </>
  );
}
